<script lang="ts">
import type { ComponentProps } from 'vue-component-type-helpers';
import { computed, defineAsyncComponent, h } from 'vue';
import FormFieldCollectionFieldSelect from '#layers/blocks/components/Form/Field/FormFieldCollectionFieldSelect.vue';

type FormFieldCollectionFieldSelectRenderProps = Omit<
	ComponentProps<typeof FormFieldCollectionFieldSelect>,
	'collection'
>;

const LazyFormFieldEjectControl = defineAsyncComponent(() => import('#layers/blocks/components/Form/Field/FormFieldEjectControl.vue'));

const __DsBlock = {
	type: 'container',
	category: 'form',
	tags: ['form-container', 'form-children'],
	icon: 'lucide:pencil',
	slots: ({ props, getParentProps }) => props.ejected || !getParentProps()?.collectionId
		? [
				{
					name: 'default',
					// TODO only support relational block if a field is a relational field
					supportedBlocks: (b) => b.type === 'input',
				},
			]
		: [],

	provide: {
		model: {
			description: 'Read or write the value of the form field.',
		},
	},

	onInserted({ setProps, getProps }) {
		const fieldId = getProps().fieldId;

		if (!fieldId) {
			setProps({
				label: 'Some Field',
			});
		}
	},

	options: ({ form, context }) => form([
		{
			name: 'name',
			label: '$t:blocks.common.props.name',
			hidden: () => !!(context.getParentProps()?.collectionId || context.getRootContext().collectionId),
		},
		{
			name: 'fieldId',
			label: '$t:blocks.common.props.field',
			hidden: () => !(context.getParentProps()?.collectionId || context.getRootContext().collectionId),
			render: (props: FormFieldCollectionFieldSelectRenderProps) => {
				const collectionId = context.getParentProps()?.collectionId || context.getRootContext().collectionId;
				const { findCollectionById } = useWorkspaceSchemaStore();
				const collection = findCollectionById(collectionId);

				if (!collection) return null;

				return h(FormFieldCollectionFieldSelect, {
					...props,
					collection,
				});
			},
		},
		{
			name: 'label',
			label: '$t:blocks.common.props.label',
		},
		{
			name: 'description',
			label: '$t:blocks.common.props.description',
		},
		{
			name: 'required',
			label: '$t:blocks.common.props.required',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'ejected',
			label: '$t:blocks.form.auto_generate_fields',
			hidden: () => !(context.getParentProps()?.collectionId || context.getRootContext().collectionId),
			render: LazyFormFieldEjectControl,
		},
	]),
}</script>

<script lang="ts" setup>
import type { GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import { pick } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import PrimitiveFormField from '#layers/blocks/components/Form/Field/PrimitiveFormField.vue';
import RelationFormField from '#layers/blocks/components/Form/Field/RelationFormField.vue';
import { useWorkspaceSchemaStore } from '#layers/data-model/public';
import { provideBaseFormFieldContext } from '#layers/forms/public';

interface FormFieldBlockProps {
	/**
	 * Name of the field to match errors.
	 */
	name?: string;

	fieldId?: GlobalFieldId;

	label?: string;
	description?: string;
	required?: boolean;
	/**
	 * By default the form field automatically render the appropriate input based on the collection settings. You can eject the field to customize it.
	 */
	ejected?: boolean;

	/**
	 * Disables this form field (and nested controls)
	 */
	disabled?: boolean;

	/**
	 * Provide a disabled notice that is shown to the user
	 */
	disabledNotice?: string;
}

// An unset `required` defers to the form's write context, so it must not be cast to `false`.
const props = withDefaults(defineProps<FormFieldBlockProps>(), { required: undefined });

;

const { findPrimitiveFieldById, findSingleRelationFieldById } = useWorkspaceSchemaStore();
const primitiveField = computed(() => props.fieldId ? findPrimitiveFieldById(props.fieldId as GlobalPrimitiveFieldId) : undefined);
const relationField = computed(() => props.fieldId ? findSingleRelationFieldById(props.fieldId as GlobalSingleRelationFieldId) : undefined);
const fieldName = computed(() => props.name ?? primitiveField.value?.apiName ?? relationField.value?.apiName);

const forwarded = computed(() => ({
	...pick(props, ['name', 'label', 'description', 'required', 'ejected']),
	'data-field-id': props.fieldId,
	'data-field-name': fieldName.value,
}));

provideBaseFormFieldContext({
	disabled: computed(() => props.disabled),
	disabledNotice: computed(() => props.disabledNotice),
});
</script>

<template>
	<PrimitiveFormField v-if="primitiveField" v-bind="forwarded" :primitive-field>
		<slot />
	</PrimitiveFormField>
	<RelationFormField v-else-if="relationField" v-bind="forwarded" :relation-field>
		<slot />
	</RelationFormField>
	<PrimitiveFormField
		v-else
		v-bind="forwarded"
		ejected
	>
		<slot />
	</PrimitiveFormField>
</template>
